import {createFeatureSelector, createSelector} from '@ngrx/store';
import {StockManagerState} from './stock-manager.reducer';
import {IStore} from '../../../shared/shared-models/store/store';
import {StoreHeaderMenuData} from '../../../shared-utilities/models-old/ngp-report-grid/header-menu-data';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import {selectAppliedAdvancedFilterGroup} from '../../core/store-shared-filter/shared-filter.selectors';
import {IFacetCounts, ITypeSenseApiDoc, TypesenseQueryData} from '../../../shared/shared-models/type-sense/type-sense-types';
import {
  selectAllUserStores,
  selectCurrentNavigation,
  selectSelectedStore,
} from '../../user-access/user-stores-store/user-store.selectors';
import {TLineColour} from '../../settings-new/models';
import {ISearchableFields} from '../../../shared/shared-models/type-sense/default-searchable-fields';
import {IDepartment, ISubDepartment} from '../../../shared/shared-models/stock/departments';
import {
  resolveStockManagerTypesenseSortField,
  sortStockItemsByMonthlyAverage,
} from './stock-manager.reducer.utils';
import {NavigationTabEnum} from '../../user-access/models/navigations.enums';
import {INavigate} from '../../user-access/models';
import {IPaginationData} from '../../../shared/shared-models/pagination/pagination-data';
import {buildMultiStoreSuppliers, MULTI_STORE_STATE_KEY} from '../utils/stock-manager-multi-store.utils';
import {IStockDisableAuditState} from '../models/stock-disable-audit.interface';


export const selectStockManagerState = createFeatureSelector<StockManagerState>('stock-manager');

export const selectSortingMenuEntry = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => {
    const headerMenuDataForStore = state.headerMenuData[store.storeId];
    if (!headerMenuDataForStore) {
      return null;
    }

    return Object.entries(headerMenuDataForStore).find(
      ([, item]) => item.sortType && item.sortType !== 'sort-unsort',
    ) ?? null;
  },
);

export const selectAllStockItemsForCurrentSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  selectSortingMenuEntry,
  (state: StockManagerState, store: IStore, sortingEntry) => {
    const items: StockItem[] | undefined = state.stockItems[store.storeId];
    if (!items?.length || !sortingEntry) {
      return items;
    }

    const [colId, menuItem] = sortingEntry;
    if (colId !== 'unitsMonthAvg') {
      return items;
    }

    const direction = menuItem.sortType === 'sort-asc' ? 'asc' : 'desc';
    return sortStockItemsByMonthlyAverage(items, direction);
  },
);

export const selectClientFilteredStockItemsCacheForSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.clientFilteredStockItemsCache[store.storeId] ?? [],
);

export const selectIsClientFilteredStockPaginationActive = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore): boolean =>
    Array.isArray(state.clientFilteredStockItemsCache[store.storeId]),
);


export const selectPaginationDataForUserSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.paginationData?.[store.storeId] ?? {
    currentPage: 1,
    pageSize: 100,
    totalItems: 0,
    searchedValue: '',
  },
);

export const selectIsStockManagerLoading = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.isStockManagerLoading,
);

export const selectIsStockItemFilterLoading = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.isStockItemFilterLoading,
);

export const selectIsStockItemClientFilterLoading = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.isStockItemClientFilterLoading,
);

export const selectIsStockGridFilterLoading = createSelector(
  selectIsStockItemFilterLoading,
  selectIsStockItemClientFilterLoading,
  (isFilterLoading: boolean, isClientFilterLoading: boolean): boolean => isFilterLoading || isClientFilterLoading,
);

export const selectIsStockManagerReloading = createSelector(
  selectIsStockManagerLoading,
  selectIsStockGridFilterLoading,
  (isStockManagerLoading: boolean, isGridFilterLoading: boolean): boolean => isStockManagerLoading || isGridFilterLoading,
);

export const selectIsStockItemsLoading = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.isStockItemsLoading,
);

export const selectBulkChangeStatusMessage = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.bulkChangeStatusMessage,
);

export const selectBulkChangeExcludedItems = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.bulkChangeExcludedItems,
);

export const selectIsBulkChangePreparing = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.isBulkChangePreparing,
);

export const selectIsBulkChangeSubmitting = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.isBulkChangeSubmitting,
);

export const selectIsStockItemsUpdating = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.isStockItemsUpdating,
);

export const selectStockUpdatesInFlight = createSelector(
  selectStockManagerState,
  (state: StockManagerState): number => state.stockUpdatesInFlight ?? 0,
);

export const selectStockUpdateBatchResults = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.stockUpdateBatchResults ?? [],
);

export const selectHaveStockItemsUpdated = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.haveStockItemsUpdated,
);

export const selectSearchableFields = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.searchableFields,
);

export const selectSuppliersForUserSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.storeSuppliers[store.storeId],
);

export const selectAllStoreSuppliers = createSelector(
  selectStockManagerState,
  (state: StockManagerState): Record<string, ISearchableFields> => state.storeSuppliers || {},
);

export const selectAllLineColours = createSelector(
  selectStockManagerState,
  (state: StockManagerState): Record<string, TLineColour> => state.lineColours || {},
);

export const selectFacetCountsForUserSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.facetCounts[store.storeId],
);

export const selectDepAndSubDepForUserSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.departments[store.storeId],
);

export const selectAllStoreDepartments = createSelector(
  selectStockManagerState,
  (state: StockManagerState): Record<string, IDepartment[]> => state.departments || {},
);

export const selectStoreSettingsForSelectedUserStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.storeSettings[store.storeId],
);

export const selectFiltersForUserSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.filters[store.storeId],
);

export const selectStockDisableAudit = createSelector(
  selectStockManagerState,
  (state: StockManagerState): IStockDisableAuditState => state.disableAudit,
);

export const selectDisableAuditNotice = createSelector(
  selectStockManagerState,
  (state: StockManagerState): string | null => state.disableAuditNotice,
);

export const selectDisableAuditVisibleCodes = createSelector(
  selectStockManagerState,
  (state: StockManagerState): string[] | null => state.disableAuditVisibleCodes,
);

export const selectItemDisablingRules = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.disabledRules?.[store.storeId],
);

export const selectEditedItems = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => {
    const editedForStore = state.editedItems[store.storeId];
    if (!editedForStore) {
      return editedForStore;
    }
    return Object.keys(editedForStore).reduce<Record<string, StockItem>>((acc, key: string) => {
      acc[String(key)] = editedForStore[key];
      return acc;
    }, {});
  },
);

export const selectEditedItemsForPreview = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => {
    return state.editedItems?.[store.storeId] ? Object.values(state.editedItems?.[store.storeId]) : undefined;
  },
);


export const selectStockMangerMenuData = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state ? state.headerMenuData : {},
);

export const selectStockManagerMenuDataByStoreId = createSelector(
  selectStockMangerMenuData,
  selectSelectedStore,
  (headerMenuData: StoreHeaderMenuData, selectedStore: IStore) => {
    if (!selectedStore?.storeId || !headerMenuData[selectedStore.storeId]) {
      return {} as StoreHeaderMenuData;
    } else {
      return headerMenuData[selectedStore.storeId];
    }
  },
);

export const selectStockMangerVisibleFields = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state ? state.visibleFields : {},
);

// Optimized: Split complex selector into smaller, memoized parts
export const selectHeaderMenuDataForSelectedStore = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore) => state.headerMenuData[store.storeId],
);

export const selectStockManagerSortType = createSelector(
  selectSortingMenuEntry,
  (matchingEntry) => {
    if (matchingEntry) {
      const [key, item] = matchingEntry;
      const typesenseField = resolveStockManagerTypesenseSortField(key);
      if (!typesenseField) {
        return '1:asc';
      }
      const sortType = item.sortType === 'sort-asc' ? 'asc' : 'desc';
      return `${typesenseField}:${sortType}`;
    } else {
      return '1:asc';
    }
  },
);

export const selectTypesenseQueryData = createSelector(
  selectSelectedStore,
  selectPaginationDataForUserSelectedStore,
  selectSearchableFields,
  selectStockManagerSortType,
  selectFiltersForUserSelectedStore,
  selectAppliedAdvancedFilterGroup,
  selectSuppliersForUserSelectedStore,
  (
    store,
    paginationData,
    searchableFields,
    sortType,
    filters,
    advancedFilterGroup,
    suppliers,
  ): TypesenseQueryData => {
    return {
      store,
      paginationData,
      searchableFields,
      sortType,
      filters,
      advancedFilterGroup,
      suppliers,
    };
  },
);

// ====================================================================================================
// Get Line Colours For Selected Store
// ====================================================================================================
export const selectLineColours = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, selectedStore: IStore): TLineColour => state.lineColours[selectedStore.storeId],
);

// ====================================================================================================
export const selectEnablePriceBandingForStockManager = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state ? state.isPriceBanding : false,
);

export const selectShowMultiplePriceChangeNotice = createSelector(
  selectStockManagerState,
  (state: StockManagerState): boolean => state?.multiplePriceChangeNotice?.show ?? false,
);

export const selectMultiplePriceChangeNoticeAcknowledgedStoreIds = createSelector(
  selectStockManagerState,
  (state: StockManagerState): string[] => state?.multiplePriceChangeNotice?.acknowledgedStoreIds ?? [],
);

/**
 * Edited item whose derived price breakdown should be shown, if any.
 */
export const selectMultiplePriceChangePreviewItem = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore): StockItem | null => {
    const code: string | null = state?.multiplePriceChangePreviewCode ?? null;
    if (!code) {
      return null;
    }
    return state.editedItems?.[store?.storeId]?.[code] ?? null;
  },
);

export const selectAutoFillSupplierStockCodeEnabled = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.autoFillSupplierStockCodeEnabled,
);

export const selectDontAskSupplierCodeAutoFillSession = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.dontAskSupplierCodeAutoFillSession,
);

export const selectStockManagerPreviewColumns = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.previewColumnData,
);

export const selectDontDisplayModalAgain = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.dontDisplayModalAgain,
);


//#region TYPESENSE API

export const selectStockManagerApiDocs = createSelector(
  selectStockManagerState,
  (state: StockManagerState): Record<string, ITypeSenseApiDoc> => state.apiDocs ?? {},
);

export const selectSelectedStoreTypeSenseApiDocs = createSelector(
  selectStockManagerState,
  selectSelectedStore,
  (state: StockManagerState, store: IStore): ITypeSenseApiDoc =>
    state.apiDocs?.[store.storeId] ?? ({searchApiKey: undefined, createdAt: undefined} as ITypeSenseApiDoc),
);

export const selectSelectedStoreTypeSenseSearchApiKey = createSelector(
  selectSelectedStoreTypeSenseApiDocs,
  (apiDocs: ITypeSenseApiDoc): string | undefined =>
    apiDocs?.searchApiKey ?? undefined,
);

export const selectCreateNewStockItemSubmit = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => state.createNewStockItemSubmit,
);

export const selectIsCreateNewStockItemPending = createSelector(
  selectCreateNewStockItemSubmit,
  (submit) => submit.status === 'pending',
);

//#endregion

//#region MULTI-STORE

export const selectIsStockManagerMultiStoreTab = createSelector(
  selectCurrentNavigation,
  (navigation: INavigate): boolean =>
    navigation?.currentPage === 'stock-manager'
    && navigation?.currentTab === NavigationTabEnum.StockManagerMultiStore,
);

export const selectMultiStoreSelectedStoreIds = createSelector(
  selectStockManagerState,
  (state: StockManagerState): string[] => state.multiStoreSelectedStoreIds ?? [],
);

export const selectMultiStoreColourMap = createSelector(
  selectStockManagerState,
  (state: StockManagerState): Record<string, string> => state.multiStoreColourMap ?? {},
);

export const selectMultiStoreBeyondPrefixCap = createSelector(
  selectStockManagerState,
  (state: StockManagerState): boolean => state.multiStoreBeyondPrefixCap ?? false,
);

export const selectMultiStoreStockItems = createSelector(
  selectStockManagerState,
  (state: StockManagerState): StockItem[] => state.stockItems[MULTI_STORE_STATE_KEY] ?? [],
);

export const selectMultiStorePaginationData = createSelector(
  selectStockManagerState,
  (state: StockManagerState): IPaginationData => state.paginationData?.[MULTI_STORE_STATE_KEY] ?? {
    currentPage: 1,
    pageSize: 100,
    totalItems: 0,
    searchedValue: '',
  },
);

export const selectMultiStoreFacetCounts = createSelector(
  selectStockManagerState,
  (state: StockManagerState): IFacetCounts => state.facetCounts[MULTI_STORE_STATE_KEY] ?? {},
);

export const selectMultiStoreSuppliers = createSelector(
  selectAllStoreSuppliers,
  selectMultiStoreSelectedStoreIds,
  selectAllUserStores,
  (suppliersByStore: Record<string, ISearchableFields>, storeIds: string[], stores: IStore[]): ISearchableFields =>
    buildMultiStoreSuppliers(suppliersByStore, storeIds, stores),
);

export const selectMultiStoreDepartments = createSelector(
  selectAllStoreDepartments,
  selectMultiStoreSelectedStoreIds,
  (departmentsByStore: Record<string, IDepartment[]>, storeIds: string[]): IDepartment[] => {
    const merged = new Map<string, IDepartment>();
    storeIds.forEach((storeId: string) => {
      (departmentsByStore[storeId] ?? []).forEach((department: IDepartment) => {
        const existing = merged.get(department.department);
        if (!existing) {
          merged.set(department.department, {
            ...department,
            subDepartments: [...(department.subDepartments ?? [])],
          });
          return;
        }
        const subDepartments = new Map(
          existing.subDepartments.map((subDepartment: ISubDepartment) => [subDepartment.subDepartment, subDepartment]),
        );
        (department.subDepartments ?? []).forEach((subDepartment: ISubDepartment) => {
          if (!subDepartments.has(subDepartment.subDepartment)) {
            subDepartments.set(subDepartment.subDepartment, subDepartment);
          }
        });
        merged.set(department.department, {
          ...existing,
          subDepartments: [...subDepartments.values()],
        });
      });
    });
    return [...merged.values()];
  },
);

export const selectMultiStoreLineColours = createSelector(
  selectAllLineColours,
  selectMultiStoreSelectedStoreIds,
  (lineColoursByStore: Record<string, TLineColour>, storeIds: string[]): TLineColour =>
    storeIds.reduce<TLineColour>((merged: TLineColour, storeId: string) => ({
      ...merged,
      ...(lineColoursByStore[storeId] ?? {}),
    }), {}),
);

export const selectMultiStoreFilters = createSelector(
  selectStockManagerState,
  (state: StockManagerState): Record<string, ISearchableFields> =>
    state.filters[MULTI_STORE_STATE_KEY] ?? {},
);

export const selectMultiStoreSortingMenuEntry = createSelector(
  selectStockManagerState,
  (state: StockManagerState) => {
    const headerMenuDataForStore = state.headerMenuData[MULTI_STORE_STATE_KEY];
    if (!headerMenuDataForStore) {
      return null;
    }
    return Object.entries(headerMenuDataForStore).find(
      ([, item]) => item.sortType && item.sortType !== 'sort-unsort',
    ) ?? null;
  },
);

export const selectMultiStoreSortType = createSelector(
  selectMultiStoreSortingMenuEntry,
  (matchingEntry) => {
    if (matchingEntry) {
      const [key, item] = matchingEntry;
      const typesenseField = resolveStockManagerTypesenseSortField(key);
      if (!typesenseField) {
        return '1:asc';
      }
      const sortType = item.sortType === 'sort-asc' ? 'asc' : 'desc';
      return `${typesenseField}:${sortType}`;
    }
    return '1:asc';
  },
);

export const selectMultiStoreEditedItems = createSelector(
  selectStockManagerState,
  selectMultiStoreSelectedStoreIds,
  (state: StockManagerState, storeIds: string[]): StockItem[] => {
    const items: StockItem[] = [];
    (storeIds || []).forEach((storeId: string) => {
      const editedForStore = state.editedItems?.[storeId];
      if (editedForStore) {
        items.push(...Object.values(editedForStore));
      }
    });
    return items;
  },
);

export const selectMultiStoreStockItemsWithEdits = createSelector(
  selectMultiStoreStockItems,
  selectStockManagerState,
  (stockItems: StockItem[], state: StockManagerState): StockItem[] => {
    return (stockItems || []).map((stockItem: StockItem) => {
      const storeId = stockItem.storeId;
      const code = String(stockItem.code ?? '');
      const editedItem = state.editedItems?.[storeId]?.[code] ?? state.editedItems?.[storeId]?.[stockItem.code];
      if (!editedItem) {
        return stockItem;
      }
      return {
        ...stockItem,
        ...editedItem,
        isEdited: editedItem.isEdited ?? true,
      };
    });
  },
);

export const selectMultiStoreMenuData = createSelector(
  selectStockMangerMenuData,
  (headerMenuData: StoreHeaderMenuData) => headerMenuData[MULTI_STORE_STATE_KEY] ?? {},
);

/** Synthetic store used to persist multi-store grid header menu / sort under MULTI_STORE_STATE_KEY. */
export const MULTI_STORE_SYNTHETIC_STORE: IStore = {
  storeId: MULTI_STORE_STATE_KEY,
  name: 'Multi-Store',
} as IStore;

//#endregion
